import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {RequestError, Store} from '@softline/core';
import {filter, map} from 'rxjs/operators';
import {Observable} from 'rxjs';
import {CouponType} from '../../data/coupon-type';
import {
  MessageBarStore,
  ModalStore,
  QuestionResult,
  SOFTLINE_FEATURE_MESSAGE_BAR,
  SOFTLINE_FEATURE_MODAL} from '@softline/ui-core';
import {ConfirmDialogComponent} from '../../dialogs/confirm-dialog/confirm-dialog.component';
import {BackNavigable, BackNavigationService, showRequestErrors} from '@softline/application';
import {SOFTLINE_FEATURE_COUPON} from '../../coupon-app.shared';
import {CouponStore} from '../../store/coupon.store';
import {CommonModule} from '@angular/common';

import { ChipComponent } from '@softline/ui-core';
import { ChipGroupComponent } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
@Component({
    selector: 'lib-charge-coupon',
    imports: [CommonModule,
    ChipComponent,
    ChipGroupComponent,
    TranslatePipe],
    templateUrl: './charge-coupon.component.html',
    styleUrls: ['./charge-coupon.component.css']
})
export class ChargeCouponComponent implements OnInit, OnDestroy, BackNavigable {

  amounts: number[] = [10, 20, 50, 100];
  selectedIndex?: number;
  selectedAmount = 0;
  displayMode: 'new' | 'existing' = 'new';

  coupon$: Observable<CouponType> = this.store.observe(SOFTLINE_FEATURE_COUPON, CouponStore.getters.data);

  private code: number | undefined;

  constructor(
    protected router: Router,
    private store: Store,
    private activatedRoute: ActivatedRoute,
    private backNavigationService: BackNavigationService
  ) { }

  async ngOnInit(): Promise<void> {
    this.backNavigationService.set(this);
    this.activatedRoute.queryParamMap
      .pipe(
        map(o => o.get('mode') as 'new' | 'existing'),
        filter(o => !!o)
      )
      .subscribe(o => this.displayMode = o);

    const entity = this.store.get(SOFTLINE_FEATURE_COUPON, CouponStore.getters.data);

    if (!entity) {
      await this.router.navigate(['/gutscheinkarten']);
      return;
    }

    this.code = entity.code;
  }

  ngOnDestroy(): void {
    this.backNavigationService.set(undefined);
  }

  async navigateBack(): Promise<void> {
    const route = this.displayMode === 'new' ? ['/coupon'] : ['/coupon', 'existing'];
    await this.router.navigate(route);
  }

  select(index: number): void {
    if (!this.selectedIndex) {
      this.selectedIndex = index;
      this.selectedAmount = this.amounts[index];
    }

    if (this.selectedIndex && this.selectedIndex !== index) {
      this.selectedIndex = index;
      this.selectedAmount = this.amounts[index];
    }
  }

  resetChips(): void {
    this.selectedIndex = undefined;
  }

  async charge(): Promise<void> {
    if (!this.code) {
      await this.router.navigate(['/gutscheinkarten']);
      return;
    }
    const result: QuestionResult | 'YES' | 'NO' | 'CANCEL' | 'DISMISSED' = await this.store.dispatch(
      SOFTLINE_FEATURE_MODAL, ModalStore.actions.open(), {
        id: 'CONFIRM_DIALOG',
        component: ConfirmDialogComponent,
        data: { amount: this.selectedAmount, type: 'charge' },
        dismiss: { escape: false, backdrop: false, button: true }
    });
    if (result === 'CANCEL' || result === 'NO' || result === 'DISMISSED') { return; }
    await this.handleResult(result);
  }

  private async handleResult(result: QuestionResult): Promise<void> {
    if (result !== 'YES') {
      return;
    }
    try {
      await this.store.dispatch(
        SOFTLINE_FEATURE_COUPON, CouponStore.actions.transaction, {amount: this.selectedAmount, action: 'CHARGE', code: this.code}
      );
      await this.store.dispatch(SOFTLINE_FEATURE_MESSAGE_BAR, MessageBarStore.actions.success, {
        message: '#COUPONS.SUCCESS_CHARGE',
        duration: 5000
      });
      await this.router.navigate(['/gutscheinkarten']);
    } catch (e: any) {
      if (e instanceof RequestError) {
        await this.store.dispatch(SOFTLINE_FEATURE_MESSAGE_BAR, MessageBarStore.actions.error, {
          message: '#COUPONS.GENERIC_ERROR',
          duration: 3000
        });
      } else {
        showRequestErrors(this.store, e);
      }
    }
  }

  async cancelCharge(): Promise<void> {
    await this.router.navigate(['/gutscheinkarten/existing']);
  }
}
